export type PlanType = 'free' | 'pro' | 'unlimited';
export type WalletPlanType = Exclude<PlanType, 'free'>;

export interface WalletPlan {
  plan: WalletPlanType;
  periodStart: string;
  periodEnd: string;
  amountPaid: number;
}

const WALLET_PLAN_STORAGE_KEY = 'walletPlan';
const PLAN_RANK: Record<PlanType, number> = { free: 0, pro: 1, unlimited: 2 };

const readJson = <T>(key: string): T | null => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const higherPlan = (first: PlanType, second: PlanType): PlanType =>
  PLAN_RANK[first] >= PLAN_RANK[second] ? first : second;

export const storeWalletPlan = (walletPlan: WalletPlan | null | undefined) => {
  if (walletPlan) {
    localStorage.setItem(WALLET_PLAN_STORAGE_KEY, JSON.stringify(walletPlan));
  } else {
    localStorage.removeItem(WALLET_PLAN_STORAGE_KEY);
  }
};

export const getActiveWalletPlan = (): WalletPlan | null => {
  const walletPlan = readJson<WalletPlan>(WALLET_PLAN_STORAGE_KEY);
  return walletPlan && new Date(walletPlan.periodEnd) > new Date() ? walletPlan : null;
};

export type PlanInterval = 'month' | 'year';

export const getStripePlanType = (planId: string | undefined): PlanType => {
  if (planId === process.env.REACT_APP_STRIPE_UNLIMITED || planId === process.env.REACT_APP_STRIPE_UNLIMITED_MONTHLY) {
    return 'unlimited';
  }
  if (planId === process.env.REACT_APP_STRIPE_PRO || planId === process.env.REACT_APP_STRIPE_PRO_MONTHLY) {
    return 'pro';
  }
  return 'free';
};

/** Billing interval for a Stripe price id. Null for free / unknown (callers default to annual). */
export const getStripePlanInterval = (planId: string | undefined): PlanInterval | null => {
  if (!planId) return null;
  if (
    planId === process.env.REACT_APP_STRIPE_PRO_MONTHLY ||
    planId === process.env.REACT_APP_STRIPE_UNLIMITED_MONTHLY
  ) {
    return 'month';
  }
  if (
    planId === process.env.REACT_APP_STRIPE_PRO ||
    planId === process.env.REACT_APP_STRIPE_UNLIMITED
  ) {
    return 'year';
  }
  return null;
};

/**
 * The plan the signed-in curator has right now: the higher of their card (Stripe)
 * plan and an unexpired wallet-paid plan. On a shared profile, the owner's plan.
 */
export const getEffectivePlanType = (): PlanType => {
  const spotifyUser = readJson<{ isShared?: boolean; ownerPlanType?: PlanType }>('spotifyUser');
  if (spotifyUser?.isShared === true) {
    return spotifyUser.ownerPlanType || 'unlimited';
  }

  const userInfo = readJson<{ free?: boolean; planId?: string }>('userInfo');
  const stripePlan = userInfo && !userInfo.free ? getStripePlanType(userInfo.planId) : 'free';
  return higherPlan(stripePlan, getActiveWalletPlan()?.plan || 'free');
};
